import * as React from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import Select from 'modules/core/components/Select'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useAttendancePolicies } from 'modules/attendance/lib/attendancePolicies'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import calendar from 'modules/attendance/store/collections/calendar'

interface Props {
  currentMonth: moment.Moment
  submitBulkChange: (shifts: TimesheetPageShiftFragment[]) => void
  shifts: TimesheetPageShiftFragment[]
}

interface WorkableDays {
  [key: string]: string | null
}

const BulkChange = ({ currentMonth, submitBulkChange, shifts }: Props) => {
  const { data } = useAttendancePolicies()
  const canEditAttendance = !!data?.employee?._policies?.editAttendance?.can

  const bulkTypes = {
    ALL: 'all',
    WORKABLE: 'workable',
    NONE: 'none',
  }
  const [open, toggle] = useToggle()
  const [action, setAction] = React.useState(bulkTypes.ALL)

  const today = moment()
  const options = [
    {
      label: i18n.t('clock_in.bulk_action.all_days'),
      value: bulkTypes.ALL,
    },
    {
      label: i18n.t('clock_in.bulk_action.all_workable_days'),
      value: bulkTypes.WORKABLE,
    },
    {
      label: i18n.t('clock_in.bulk_action.none'),
      value: bulkTypes.NONE,
    },
  ]

  const addShiftForWorkableDays = () => {
    const workableDays = AttendanceShiftUtils.daysOfMonth(currentMonth).reduce(
      (days: WorkableDays, day) => {
        const calendarDay = calendar.find({ day: day.date() })

        if (calendarDay?.isWorkable && day.isSameOrBefore(today)) {
          const date = i18nDateToString(day, 'backend')
          days[date] = calendarDay.workableHalfDay
        }

        return days
      },
      {}
    )

    const monthShifts = shifts.filter((shift) => {
      const date = shift.date
      const notDefaultShift = AttendanceShiftUtils.isNew(shift)

      return (
        notDefaultShift &&
        date in workableDays &&
        (!workableDays[date] || workableDays[date] === shift.halfDay)
      )
    })
    submitBulkChange(monthShifts)
  }

  const addShiftForAllMonth = () => {
    const days = AttendanceShiftUtils.daysOfMonth(currentMonth)
      .filter((day) => day.isSameOrBefore(today))
      .map((day) => i18nDateToString(day, 'backend'))

    const monthShifts = shifts.filter(
      (shift) => AttendanceShiftUtils.isNew(shift) && days.includes(shift.date)
    )
    submitBulkChange(monthShifts)
  }

  const removeAllShifts = () => {
    submitBulkChange(
      shifts.filter(
        (shift) =>
          !AttendanceShiftUtils.isNew(shift) &&
          stringToDate(shift.date, 'backend').isSame(currentMonth, 'month')
      )
    )
  }

  const submit = () => {
    setAction(bulkTypes.ALL) // back to default for next use

    toggle(false)()

    switch (action) {
      case bulkTypes.ALL:
        return addShiftForAllMonth()

      case bulkTypes.WORKABLE:
        return addShiftForWorkableDays()

      case bulkTypes.NONE:
        return removeAllShifts()

      default:
    }
  }

  const content = (
    <Box alignItems="center" justifyContent="flexStart" padding="s32" gap="s16">
      <Select options={options} onChange={(value) => setAction(value)} />
      <Button onClick={submit} type="primary">
        {i18n.t('clock_in.bulk_action.apply')}
      </Button>
    </Box>
  )

  return (
    <Popover open={open} size="big" toggle={toggle} attachment="top center">
      <Button onClick={() => toggle} type="secondary" disabled={!canEditAttendance}>
        {i18n.t('clock_in.bulk_action.button')}
      </Button>
      {content}
    </Popover>
  )
}

export default observer(BulkChange)
